import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../../components/layout/Layout';

// Definição do tipo XPRule
interface XPRule {
  id: string | number;
  nome: string;
  descricao: string;
  tipo: string;
  pontos: number;
  multiplicador: number;
  limiteDiario: number | null;
  limiteUsos?: number;
  condicoes?: string;
  ativo: boolean;
  icon?: string;
  color?: string;
  usosHoje?: number;
  totalAplicacoes?: number;
}

export default function RegrasXPPage() {
  const { t } = useTranslation();
  const [abaAtiva, setAbaAtiva] = useState('acoes');
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  
  // Mock data inicial para as regras
  const mockRulesData: XPRule[] = [
    { id: 1, nome: 'Venda Realizada', descricao: 'XP por cada venda concluída', tipo: 'Venda', pontos: 100, multiplicador: 1, limiteDiario: 10, ativo: true, icon: 'ri-shopping-cart-line', color: 'bg-teal-500' },
    { id: 2, nome: 'Login Diário', descricao: 'XP por fazer login no sistema', tipo: 'Login', pontos: 10, multiplicador: 1, limiteDiario: 1, ativo: true, icon: 'ri-login-box-line', color: 'bg-blue-500' },
    { id: 3, nome: 'Meta Atingida', descricao: 'XP ao atingir meta mensal', tipo: 'Meta', pontos: 500, multiplicador: 2, limiteDiario: null, ativo: true, icon: 'ri-trophy-line', color: 'bg-yellow-500' },
  ];
  
  const [rules, setRules] = useState<XPRule[]>(mockRulesData);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<XPRule | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Estados do formulário
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    pontos: '',
    multiplicador: '1',
    limiteDiario: '',
    condicoes: '',
    descricao: '',
    ativa: true
  });

  // Estados de erro para cada campo
  const [errors, setErrors] = useState({
    nome: '',
    tipo: '',
    pontos: '',
    multiplicador: '',
    limiteDiario: '',
    condicoes: '',
    descricao: ''
  });

  // Estados para as políticas de XP
  const [politicasXP, setPoliticasXP] = useState({
    expiracao: 'nao-expira',
    diasExpiracao: '',
    unidadeExpiracao: 'dias',
    permitirRollover: true,
    aplicarPenalidade: false,
    percentualPenalidade: '',
    xpParaResgate: '100',
    valorReais: '1.00'
  });

  const [isSavingPoliticas, setIsSavingPoliticas] = useState(false);

  // Regras de XP por ação
  const regrasAcoes = [
    { id: 1, acao: 'Venda de Produto', xp: 100, tipo: 'Por venda', campanha: 'Todas', ativo: true },
    { id: 2, acao: 'Prestação de Serviço', xp: 150, tipo: 'Por serviço', campanha: 'Todas', ativo: true },
    { id: 3, acao: 'Meta Atingida', xp: 500, tipo: 'Por meta', campanha: 'Todas', ativo: true },
    { id: 4, acao: 'Ativação de Cliente', xp: 200, tipo: 'Por cliente', campanha: 'Todas', ativo: true },
    { id: 5, acao: 'Atendimento Realizado', xp: 50, tipo: 'Por atendimento', campanha: 'Todas', ativo: true },
  ];

  // XP Bônus
  const xpBonus = [
    { id: 1, nome: 'Bônus Primeira Venda do Dia', xp: 50, condicao: 'Primeira venda', ativo: true },
    { id: 2, nome: 'Bônus Meta Superada', xp: 1000, condicao: 'Meta > 120%', ativo: true },
    { id: 3, nome: 'Bônus Sequência 7 Dias', xp: 300, condicao: '7 dias consecutivos', ativo: true },
  ];

  // Limites de XP por Campanha
  const limitesCampanha = [
    { id: 1, campanha: 'Campanha Verão 2024', limite_diario: 1000, limite_mensal: 20000, ativo: true },
    { id: 2, campanha: 'Mega Vendas Q1', limite_diario: 1500, limite_mensal: 30000, ativo: true },
    { id: 3, campanha: 'Ativação Premium', limite_diario: 800, limite_mensal: 15000, ativo: true },
  ];

  // XP por Categoria de Produto
  const xpCategorias = [
    { id: 1, categoria: 'Eletrônicos', xp_base: 150, multiplicador: 1.5 },
    { id: 2, categoria: 'Informática', xp_base: 120, multiplicador: 1.3 },
    { id: 3, categoria: 'Acessórios', xp_base: 80, multiplicador: 1.0 },
  ];

  // Fator de XP para Jogos
  const fatoresJogos = [
    { id: 1, jogo: 'Roleta de Prêmios', xp_entrada: 50, xp_premio_min: 10, xp_premio_max: 500 },
    { id: 2, jogo: 'Bingo', xp_entrada: 100, xp_premio_min: 50, xp_premio_max: 1000 },
    { id: 3, jogo: 'Raspadinha', xp_entrada: 30, xp_premio_min: 5, xp_premio_max: 300 },
    { id: 4, jogo: 'Rifa', xp_entrada: 20, xp_premio_min: 100, xp_premio_max: 5000 },
    { id: 5, jogo: 'Palpites da Copa', xp_entrada: 10, xp_premio_min: 20, xp_premio_max: 200 },
  ];

  // Histórico de alterações
  const historico = [
    { id: 1, data: '15/01/2024 14:30', usuario: 'Admin', acao: 'Alterou XP de Venda de Produto', de: '80', para: '100' },
    { id: 2, data: '14/01/2024 10:15', usuario: 'Admin', acao: 'Criou Bônus Meta Superada', de: '-', para: '1000 XP' },
    { id: 3, data: '13/01/2024 16:45', usuario: 'Admin', acao: 'Alterou Limite Diário Campanha Verão', de: '800', para: '1000' },
  ];

  // Mock data para a tabela
  const filteredRules = rules;

  // Função para validar um campo específico
  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'nome':
        if (!value.trim()) {
          error = 'Nome da regra é obrigatório';
        } else if (value.trim().length < 3) {
          error = 'Nome deve ter pelo menos 3 caracteres';
        }
        break;
      case 'tipo':
        if (!value) {
          error = 'Selecione um tipo de ação';
        }
        break;
      case 'pontos':
        if (!value) {
          error = 'Pontos de XP são obrigatórios';
        } else if (isNaN(Number(value)) || Number(value) <= 0) {
          error = 'Pontos devem ser um número positivo';
        }
        break;
      case 'multiplicador':
        if (!value) {
          error = 'Multiplicador é obrigatório';
        } else if (isNaN(Number(value)) || Number(value) < 1) {
          error = 'Multiplicador deve ser no mínimo 1';
        }
        break;
      case 'limiteDiario':
        if (value && (isNaN(Number(value)) || Number(value) < 0)) {
          error = 'Limite diário deve ser um número válido';
        }
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error === '';
  };

  // Função para validar todo o formulário
  const validateForm = () => {
    const newErrors = {
      nome: '',
      tipo: '',
      pontos: '',
      multiplicador: '',
      limiteDiario: '',
      condicoes: '',
      descricao: ''
    };

    let isValid = true;

    // Validar nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome da regra é obrigatório';
      isValid = false;
    } else if (formData.nome.trim().length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
      isValid = false;
    }

    // Validar tipo
    if (!formData.tipo) {
      newErrors.tipo = 'Selecione um tipo de ação';
      isValid = false;
    }

    // Validar pontos
    if (!formData.pontos) {
      newErrors.pontos = 'Pontos de XP são obrigatórios';
      isValid = false;
    } else if (isNaN(Number(formData.pontos)) || Number(formData.pontos) <= 0) {
      newErrors.pontos = 'Pontos devem ser um número positivo';
      isValid = false;
    }

    // Validar multiplicador
    if (!formData.multiplicador) {
      newErrors.multiplicador = 'Multiplicador é obrigatório';
      isValid = false;
    } else if (isNaN(Number(formData.multiplicador)) || Number(formData.multiplicador) < 1) {
      newErrors.multiplicador = 'Multiplicador deve ser no mínimo 1';
      isValid = false;
    }

    // Validar limite diário
    if (formData.limiteDiario && (isNaN(Number(formData.limiteDiario)) || Number(formData.limiteDiario) < 0)) {
      newErrors.limiteDiario = 'Limite diário deve ser um número válido';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Função para lidar com mudanças nos campos
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validar o campo ao digitar (limpar erro se estiver válido)
    if (errors[name as keyof typeof errors]) {
      validateField(name, value);
    }
  };

  // Função para lidar com blur (quando o usuário sai do campo)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar todo o formulário
    if (!validateForm()) {
      showToastMessage('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simular salvamento (aqui você conectaria com o backend)
      await new Promise(resolve => setTimeout(resolve, 1500));

      const newRule: XPRule = {
        id: editingRule?.id || Date.now().toString(),
        nome: formData.nome.trim(),
        tipo: formData.tipo,
        pontos: Number(formData.pontos),
        multiplicador: Number(formData.multiplicador),
        limiteDiario: formData.limiteDiario ? Number(formData.limiteDiario) : undefined,
        condicoes: formData.condicoes.trim() || undefined,
        descricao: formData.descricao.trim(),
        ativa: formData.ativa,
        dataCriacao: editingRule?.dataCriacao || new Date().toISOString().split('T')[0],
        ultimaAtualizacao: new Date().toISOString().split('T')[0]
      };

      if (editingRule) {
        // Atualizar regra existente
        setRules(rules.map(r => r.id === editingRule.id ? newRule : r));
        showToastMessage('Regra atualizada com sucesso!', 'success');
      } else {
        // Adicionar nova regra
        setRules([...rules, newRule]);
        showToastMessage('Regra criada com sucesso!', 'success');
      }

      // Resetar formulário e fechar modal
      resetForm();
      setShowModal(false);
      setEditingRule(null);
    } catch (error) {
      showToastMessage('Erro ao salvar regra. Tente novamente.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: '',
      pontos: '',
      multiplicador: '1',
      limiteDiario: '',
      condicoes: '',
      descricao: '',
      ativa: true
    });
    setErrors({
      nome: '',
      tipo: '',
      pontos: '',
      multiplicador: '',
      limiteDiario: '',
      condicoes: '',
      descricao: ''
    });
  };

  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleEdit = (rule: XPRule) => {
    setEditingRule(rule);
    setFormData({
      nome: rule.nome,
      tipo: rule.tipo,
      pontos: rule.pontos.toString(),
      multiplicador: rule.multiplicador.toString(),
      limiteDiario: rule.limiteDiario?.toString() || '',
      condicoes: rule.condicoes || '',
      descricao: rule.descricao,
      ativa: rule.ativa
    });
    setErrors({
      nome: '',
      tipo: '',
      pontos: '',
      multiplicador: '',
      limiteDiario: '',
      condicoes: '',
      descricao: ''
    });
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta regra?')) {
      setRules(rules.filter(r => r.id !== id));
      showToastMessage('Regra excluída com sucesso!', 'success');
    }
  };

  const handleNewRule = () => {
    resetForm();
    setEditingRule(null);
    setShowModal(true);
  };

  const handleEditRule = (rule: XPRule) => {
    setEditingRule(rule);
    setFormData({
      nome: rule.nome,
      tipo: rule.tipo,
      pontos: rule.pontos.toString(),
      multiplicador: rule.multiplicador.toString(),
      limiteDiario: rule.limiteDiario?.toString() || '',
      condicoes: rule.condicoes || '',
      descricao: rule.descricao,
      ativa: rule.ativo
    });
    setErrors({
      nome: '',
      tipo: '',
      pontos: '',
      multiplicador: '',
      limiteDiario: '',
      condicoes: '',
      descricao: ''
    });
    setShowModal(true);
  };

  const handleDeleteRule = (id: string | number) => {
    if (window.confirm('Tem certeza que deseja excluir esta regra?')) {
      setRules(rules.filter(r => r.id !== id));
      showToastMessage('Regra excluída com sucesso!', 'success');
    }
  };

  const handleSavePoliticas = async () => {
    // Validar campos obrigatórios
    if (politicasXP.expiracao === 'expira-periodo' && !politicasXP.diasExpiracao) {
      showToastMessage('Por favor, informe o período de expiração', 'error');
      return;
    }

    if (politicasXP.aplicarPenalidade && !politicasXP.percentualPenalidade) {
      showToastMessage('Por favor, informe o percentual de penalidade', 'error');
      return;
    }

    if (!politicasXP.xpParaResgate || Number(politicasXP.xpParaResgate) <= 0) {
      showToastMessage('Por favor, informe um valor válido de XP para resgate', 'error');
      return;
    }

    if (!politicasXP.valorReais || Number(politicasXP.valorReais) <= 0) {
      showToastMessage('Por favor, informe um valor válido em reais', 'error');
      return;
    }

    setIsSavingPoliticas(true);

    try {
      // Simular salvamento (aqui você conectaria com o backend)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Aqui você salvaria as políticas no backend
      console.log('Políticas salvas:', politicasXP);

      showToastMessage('Políticas de XP salvas com sucesso!', 'success');
    } catch (error) {
      showToastMessage('Erro ao salvar políticas. Tente novamente.', 'error');
    } finally {
      setIsSavingPoliticas(false);
    }
  };

  return (
    <Layout>
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 ${
            toastType === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
            <i className={`${toastType === 'success' ? 'ri-check-line' : 'ri-close-line'} text-xl`}></i>
            <span className="font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Regras de XP</h1>
            <p className="text-sm text-gray-600 mt-1">Configuração de regras de ganho e perda de XP (moeda exclusiva da gamificação)</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHistoricoModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-history-line mr-2"></i>
              Histórico de Alterações
            </button>
            <button
              onClick={handleNewRule}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Nova Regra
            </button>
          </div>
        </div>

        {/* Abas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex gap-1 p-1">
              {[
                { id: 'acoes', label: 'XP por Ação', icon: 'ri-flashlight-line' },
                { id: 'bonus', label: 'XP Bônus', icon: 'ri-gift-line' },
                { id: 'limites', label: 'Limites por Campanha', icon: 'ri-shield-check-line' },
                { id: 'categorias', label: 'XP por Categoria', icon: 'ri-price-tag-3-line' },
                { id: 'jogos', label: 'Fator de Jogos', icon: 'ri-gamepad-line' },
                { id: 'politicas', label: 'Políticas de XP', icon: 'ri-settings-3-line' },
              ].map((aba) => (
                <button
                  key={aba.id}
                  onClick={() => setAbaAtiva(aba.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    abaAtiva === aba.id
                      ? 'bg-teal-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${aba.icon} mr-2`}></i>
                  {aba.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Aba: XP por Ação */}
            {abaAtiva === 'acoes' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">XP Ganho por Tipo de Ação</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Ação</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">XP</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Campanha</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {regrasAcoes.map((regra) => (
                        <tr key={regra.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{regra.acao}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold">
                              <i className="ri-star-fill mr-1"></i>
                              {regra.xp} XP
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{regra.tipo}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{regra.campanha}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              regra.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {regra.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                                <i className="ri-edit-line text-base"></i>
                              </button>
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                                <i className="ri-file-copy-line text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Aba: XP Bônus */}
            {abaAtiva === 'bonus' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">XP Bônus</h3>
                <div className="space-y-3">
                  {xpBonus.map((bonus) => (
                    <div key={bonus.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{bonus.nome}</p>
                        <p className="text-xs text-gray-600 mt-1">Condição: {bonus.condicao}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-bold">
                          <i className="ri-star-fill mr-1"></i>
                          +{bonus.xp} XP
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          bonus.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {bonus.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                        <div className="flex items-center gap-2">
                          <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aba: Limites por Campanha */}
            {abaAtiva === 'limites' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Limites de XP por Campanha</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Campanha</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Limite Diário</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Limite Mensal</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {limitesCampanha.map((limite) => (
                        <tr key={limite.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{limite.campanha}</td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-yellow-600">{limite.limite_diario.toLocaleString('pt-BR')} XP</td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-yellow-600">{limite.limite_mensal.toLocaleString('pt-BR')} XP</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              limite.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {limite.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                                <i className="ri-edit-line text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Aba: XP por Categoria */}
            {abaAtiva === 'categorias' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">XP por Categoria de Produto</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Categoria</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">XP Base</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Multiplicador</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xpCategorias.map((cat) => (
                        <tr key={cat.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{cat.categoria}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold">
                              {cat.xp_base} XP
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-teal-600">x{cat.multiplicador}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                                <i className="ri-edit-line text-base"></i>
                              </button>
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                                <i className="ri-delete-bin-line text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Aba: Fator de Jogos */}
            {abaAtiva === 'jogos' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Fator de XP para Jogos</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Jogo</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">XP Entrada</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">XP Prêmio Mín</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">XP Prêmio Máx</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fatoresJogos.map((jogo) => (
                        <tr key={jogo.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{jogo.jogo}</td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-red-600">{jogo.xp_entrada} XP</td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-green-600">{jogo.xp_premio_min} XP</td>
                          <td className="py-3 px-4 text-center text-sm font-bold text-green-600">{jogo.xp_premio_max} XP</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors">
                                <i className="ri-edit-line text-base"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Aba: Políticas de XP */}
            {abaAtiva === 'politicas' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Políticas de XP</h3>
                
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Expiração de XP</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="radio" 
                        name="expiracao" 
                        checked={politicasXP.expiracao === 'nao-expira'}
                        onChange={() => setPoliticasXP(prev => ({ ...prev, expiracao: 'nao-expira' }))}
                        className="w-4 h-4 text-teal-600" 
                      />
                      <span className="text-sm text-gray-700">XP não expira (acumula indefinidamente)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="radio" 
                        name="expiracao" 
                        checked={politicasXP.expiracao === 'expira-periodo'}
                        onChange={() => setPoliticasXP(prev => ({ ...prev, expiracao: 'expira-periodo' }))}
                        className="w-4 h-4 text-teal-600" 
                      />
                      <span className="text-sm text-gray-700">XP expira após período determinado</span>
                    </label>
                    {politicasXP.expiracao === 'expira-periodo' && (
                      <div className="ml-7 grid grid-cols-2 gap-4">
                        <input 
                          type="number" 
                          placeholder="Dias" 
                          value={politicasXP.diasExpiracao}
                          onChange={(e) => setPoliticasXP(prev => ({ ...prev, diasExpiracao: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" 
                        />
                        <select 
                          value={politicasXP.unidadeExpiracao}
                          onChange={(e) => setPoliticasXP(prev => ({ ...prev, unidadeExpiracao: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="dias">Dias</option>
                          <option value="meses">Meses</option>
                          <option value="anos">Anos</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Rollover de XP</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={politicasXP.permitirRollover}
                        onChange={(e) => setPoliticasXP(prev => ({ ...prev, permitirRollover: e.target.checked }))}
                        className="w-4 h-4 text-teal-600 rounded" 
                      />
                      <span className="text-sm text-gray-700">Permitir rollover de XP entre campanhas</span>
                    </label>
                    {politicasXP.permitirRollover && (
                      <>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={politicasXP.aplicarPenalidade}
                            onChange={(e) => setPoliticasXP(prev => ({ ...prev, aplicarPenalidade: e.target.checked }))}
                            className="w-4 h-4 text-teal-600 rounded" 
                          />
                          <span className="text-sm text-gray-700">Aplicar penalidade no rollover</span>
                        </label>
                        {politicasXP.aplicarPenalidade && (
                          <div className="ml-7">
                            <input 
                              type="number" 
                              placeholder="% de penalidade" 
                              value={politicasXP.percentualPenalidade}
                              onChange={(e) => setPoliticasXP(prev => ({ ...prev, percentualPenalidade: e.target.value }))}
                              min="0"
                              max="100"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" 
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">Fator de Conversão de XP</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">XP Exigido para Resgate</label>
                      <input 
                        type="number" 
                        placeholder="100" 
                        value={politicasXP.xpParaResgate}
                        onChange={(e) => setPoliticasXP(prev => ({ ...prev, xpParaResgate: e.target.value }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Equivale a (R$)</label>
                      <input 
                        type="number" 
                        placeholder="1,00" 
                        value={politicasXP.valorReais}
                        onChange={(e) => setPoliticasXP(prev => ({ ...prev, valorReais: e.target.value }))}
                        min="0.01"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" 
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    <i className="ri-information-line mr-1"></i>
                    Esta conversão é apenas para referência de valor. XP e Pontos são moedas separadas.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button 
                    onClick={handleSavePoliticas}
                    disabled={isSavingPoliticas}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSavingPoliticas ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line"></i>
                        <span>Salvar Políticas</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Histórico */}
        {showHistoricoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Histórico de Alterações</h2>
                <button
                  onClick={() => setShowHistoricoModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  {historico.map((item) => (
                    <div key={item.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{item.acao}</p>
                          <p className="text-xs text-gray-600 mt-1">
                            {item.de !== '-' && `De: ${item.de} → `}Para: {item.para}
                          </p>
                        </div>
                        <span className="text-xs text-gray-600">{item.data}</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        <i className="ri-user-line mr-1"></i>
                        {item.usuario}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Criação/Edição de Regra */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingRule ? 'Editar Regra' : 'Nova Regra de XP'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingRule(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-5">
                  {/* Nome da Regra */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Regra <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                        errors.nome ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ex: Venda Realizada"
                    />
                    {errors.nome && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.nome}
                      </p>
                    )}
                  </div>

                  {/* Tipo de Ação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Ação <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                        errors.tipo ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecione o tipo</option>
                      <option value="venda">Venda</option>
                      <option value="cadastro">Cadastro</option>
                      <option value="login">Login Diário</option>
                      <option value="indicacao">Indicação</option>
                      <option value="treinamento">Treinamento</option>
                      <option value="meta">Meta Atingida</option>
                      <option value="outro">Outro</option>
                    </select>
                    {errors.tipo && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.tipo}
                      </p>
                    )}
                  </div>

                  {/* Pontos e Multiplicador */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pontos de XP <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="pontos"
                        value={formData.pontos}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        min="1"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          errors.pontos ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="100"
                      />
                      {errors.pontos && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.pontos}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Multiplicador <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="multiplicador"
                        value={formData.multiplicador}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        min="1"
                        step="0.1"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          errors.multiplicador ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="1.0"
                      />
                      {errors.multiplicador && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.multiplicador}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Limite Diário */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Limite Diário (opcional)
                    </label>
                    <input
                      type="number"
                      name="limiteDiario"
                      value={formData.limiteDiario}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      min="0"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed ${
                        errors.limiteDiario ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 5 (deixe vazio para ilimitado)"
                    />
                    {errors.limiteDiario && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <i className="ri-error-warning-line mr-1"></i>
                        {errors.limiteDiario}
                      </p>
                    )}
                  </div>

                  {/* Condições */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Condições (opcional)
                    </label>
                    <input
                      type="text"
                      name="condicoes"
                      value={formData.condicoes}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Ex: Valor mínimo R$ 1.000"
                    />
                  </div>

                  {/* Descrição */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                      placeholder="Descreva quando esta regra deve ser aplicada..."
                    />
                  </div>

                  {/* Status */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="ativa"
                      checked={formData.ativa}
                      onChange={(e) => setFormData(prev => ({ ...prev, ativa: e.target.checked }))}
                      disabled={isSubmitting}
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 disabled:cursor-not-allowed"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Regra ativa
                    </label>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingRule(null);
                      resetForm();
                    }}
                    disabled={isSubmitting}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line"></i>
                        <span>{editingRule ? 'Salvar Alterações' : 'Criar Regra'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tabela de Regras */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Regra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    XP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Limite
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${rule.color} flex items-center justify-center`}>
                          <i className={`${rule.icon} text-white text-lg`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{rule.nome}</div>
                          <div className="text-xs text-gray-500">{rule.descricao}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {rule.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {rule.pontos} XP
                      </div>
                      {rule.multiplicador > 1 && (
                        <div className="text-xs text-teal-600">
                          x{rule.multiplicador}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {rule.limiteDiario ? `${rule.limiteDiario}/dia` : 'Ilimitado'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rule.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditRule(rule)}
                          className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
